import axios from "axios";
import { useCallback } from "react";
import {
  REACT_APP_ACR_API_BASE_URL,
  REACT_APP_ACR_API_PROTOCOL,
} from "../utlities/core.constants";

const BASE = `${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}`;
const CATALOG_API = `${BASE}/api/catalog`;
const STANDARD_API = `${BASE}/api/standard`;

/** Turn "PartiallySupports" into "Partially Supports" for display. */
function formatConformanceLabel(name) {
  if (!name || typeof name !== "string") return String(name ?? "");
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^\s+/, "")
    .trim();
}

/**
 * Catalog (from list): id, name, vpatEdition, description, standards: [standardIds], ...
 * Standard (fetched by id): name, version, description, chapters: [{ name, description, criteria: [criteriaIds], isActive }, ...]
 */
export default function useCatalogApi() {
  const getCatalogsList = useCallback(async (pageSize = 20, pageNumber = 1) => {
    try {
      const response = await axios.get(`${CATALOG_API}/all?pageSize=${pageSize}&pageNumber=${pageNumber}`);
      const body = response?.data;
      return Array.isArray(body) ? body : body?.data ?? [];
    } catch (err) {
      return [];
    }
  }, []);

  const getCatalogById = useCallback(async (id) => {
    try {
      const response = await axios.get(`${CATALOG_API}?id=${encodeURIComponent(id)}`);
      const body = response?.data;
      return body?.data ?? body ?? null;
    } catch (err) {
      return null;
    }
  }, []);

  const getCompleteCatalogById = useCallback(async (id) => {
    try {
      const response = await axios.get(`${CATALOG_API}/complete?id=${encodeURIComponent(id)}`);
      const body = response?.data;
      return body?.data ?? body ?? null;
    } catch (err) {
      return null;
    }
  }, []);

  /**
   * Fetch conformance levels from GET /api/catalog/conformance-levels.
   * API returns [{ name }, ...]. We return [{ name, label }] so name (enum) is the option value and label is for display.
   */
  const getConformanceLevels = useCallback(async () => {
    try {
      const response = await axios.get(`${CATALOG_API}/conformance-levels`);
      const list = response?.data;
      if (!Array.isArray(list)) return [];
      return list.map((item) => ({
        name: item.name,
        label: formatConformanceLabel(item.name),
      }));
    } catch (err) {
      return [];
    }
  }, []);

  /** Fetch one standard by id. Returns { name, version, description, chapters: [...], ... } or null. */
  const getStandardById = useCallback(async (id) => {
    try {
      const response = await axios.get(`${STANDARD_API}?id=${encodeURIComponent(id)}`);
      const body = response?.data;
      return body?.data ?? body ?? null;
    } catch (err) {
      return null;
    }
  }, []);

  return {
    getCatalogsList,
    getCatalogById,
    getStandardById,
    getCompleteCatalogById,
    getConformanceLevels,
  };
}
